<!--
* A navigation bar component
*
* @param {Object | Object[]} buttons - Array of button objects
* @param {string} button.id - Button id
* @param {string} button.text - Text in button
* @param {boolean} button.hasIconLeft - Whether button has icon on left
* @param {boolean} button.hasIconRight - Whether button has icon on right
* @param {boolean} button.pullRight - Whether button should be pulled to right
 *                                    (NOTE: only applies if singular button)
* @param {ButtonStates} [button.state=ButtonStates.ENABLED] - Button state
* @param {buttonTypes} [button.type=ButtonTypes.PRIMARY] - Button type
*
* @fires MsNavigationBar#navigationButtonClick
-->

<!--
* @event MsNavigationBar#navigationButtonClick
* @property {string} buttonId ID of clicked button
-->

<!-- @Todo Change button object to be defined as a class -->
<template>
  <div
    class="ms-navigation-bar">

    <!-- Navigation buttons -->
    <div
      class="ms-navigation"
      :class="{
        'single-button-pull-right': button.pullRight,
        'has-max-width': button.hasMaxWidth
      }"
      v-for="button in buttons"
      :key="button.id">
      <AppButton
        class="ms-navigation-button"
        :text="$t(button.text)"
        :state="getButtonState(button.state)"
        :type="getButtonType(button.type)"
        :hasIconLeft="button.hasIconLeft"
        :hasIconRight="button.hasIconRight"
        @click.native="onClick(button.id)"/>
    </div>
  </div>
</template>

<script>
  import AppButton, {ButtonTypes, ButtonStates} from '@/components/app-button'

  /**
   * Default back / next navigation buttons
   */
  // @todo move to appsettings
  export const DefaultNavigationButtons = {
    BACK: {
      state: ButtonStates.ENABLED,
      type: ButtonTypes.TERTIARY,
      text: 'message.backButton',
      hasMaxWidth: true,
      hasIconLeft: true,
      id: 'back'
    },
    NEXT: {
      type: ButtonStates.PRIMARY,
      text: 'message.nextButton',
      hasMaxWidth: true,
      hasIconRight: true,
      id: 'next'
    }
  }

  export default {
    components: {AppButton},
    name: 'MsNavigationBar',
    props: {
      buttons: {
        type: [Object, Array],
        required: true
      }
    },
    computed: {
      /**
       * Checks whether there's 1 button and it should be pulled to right
       */
      pullRight: {
        get () {
          return this.buttons.length === 1 && this.button.pullRight
        }
      }
    },
    methods: {
      /**
       * Get button state
       *
       * @param {string} state - Button state
       * @returns {string} state prop or if undefined returns default
       */
      getButtonState (state) {
        return state !== undefined ? state : ButtonStates.ENABLED
      },

      /**
       * Get button type
       *
       * @param {string} type - button type
       * @returns {string} type prop or if undefined returns default
       */
      getButtonType (type) {
        return type !== undefined ? type : ButtonTypes.PRIMARY
      },

      /**
       * Emit navigationButtonClick event on button click
       *
       * @param {string} navigationClass - Navigation class
       */
      onClick (buttonName) {
        this.$emit('navigationButtonClick', buttonName)
      }
    }
  }
</script>

<style scoped lang="scss">
  @import "../assets/sass/shared.scss";

  .ms-navigation-bar {
    display: flex;
    justify-content: space-between;
  }

  .ms-navigation {
    width: calc(50% - 5px);

    &.has-max-width {
      max-width: $dims-button-max-width;
    }
  }

  .ms-navigation-button {
    width: 100%;
    padding: $dims-spacing-small $dims-spacing-large;
    position: relative;
  }

  ::v-deep .app_button__icon_right {
    position: absolute;
    right: $dims-spacing-small;
    top: 50%;
    transform: translateY(-50%);
  }

  ::v-deep .app_button__icon_left {
    position: absolute;
    left: $dims-spacing-small;
    top: 50%;
    transform: translateY(-50%);
  }

  .single-button-pull-right {
    margin-left: auto;
    margin-right: 0;
  }

</style>
